import {
  ModButton,
  ModModal,
  ModModalContent,
  ModModalFooter,
  ModModalHeader
} from '@moderneinc/design-system-components';
import { spacing } from '@moderneinc/design-system-tokens';
import { Stack, Typography } from '@mui/material';
import {
  type FunctionComponent,
  useCallback,
  useEffect,
  useState
} from 'react';
import {
  orgSelectorClosedEvent,
  orgSelectorEvent,
  orgSelectorMissingOrgClickEvent
} from '../../../hooks/use-app-event.hooks';
import { useSelectedOrganization } from '../../../hooks/use-organizations.hooks';

/**
 * Recovery modal shown when the user's selected organization can no longer be
 * loaded (deleted or access revoked). Hides itself while the org selector is
 * open and reappears if the user dismisses the selector without picking a
 * valid org. The user may also dismiss the modal directly to continue using
 * the rest of the app; it returns the next time the missing-org state is
 * re-entered.
 */
export const MissingOrganizationModal: FunctionComponent = () => {
  const { isMissingOrg, lastKnownOrgName } = useSelectedOrganization();
  const [hiddenForSelector, setHiddenForSelector] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isMissingOrg) {
      setHiddenForSelector(false);
      setDismissed(false);
    }
  }, [isMissingOrg]);

  orgSelectorClosedEvent.useListener(() => {
    setHiddenForSelector(false);
  });

  orgSelectorMissingOrgClickEvent.useListener(() => {
    setDismissed(false);
    setHiddenForSelector(false);
  });

  const handleClose = useCallback(() => {
    setDismissed(true);
  }, []);

  const handleSwitchClick = useCallback(() => {
    setHiddenForSelector(true);
    orgSelectorEvent.dispatch();
  }, []);

  return (
    <ModModal
      open={isMissingOrg && !hiddenForSelector && !dismissed}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <ModModalHeader
        title="This organization is no longer available"
        onClose={handleClose}
      />
      <ModModalContent>
        <Stack sx={{ gap: `${spacing.spacing_1}px` }}>
          {lastKnownOrgName && (
            <Typography variant="body" sx={{ wordBreak: 'break-word' }}>
              {lastKnownOrgName}
            </Typography>
          )}
          <Typography variant="body">
            It may have been deleted or you no longer have access.
          </Typography>
        </Stack>
      </ModModalContent>
      <ModModalFooter>
        <ModButton variant="primary" onClick={handleSwitchClick}>
          Switch organization
        </ModButton>
      </ModModalFooter>
    </ModModal>
  );
};
